import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../services/api';
import ToastService from '../../core/notifications/ToastService';

export const CreateSlotPage: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    capacity: '',
    price_coins: '',
    price_cash: '',
    description: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.date || !formData.start_time || !formData.end_time || !formData.capacity || !formData.price_coins || !formData.price_cash) {
      setError('All required fields must be filled');
      return false;
    }

    if (formData.start_time >= formData.end_time) {
      setError('End time must be after start time');
      return false;
    }

    if (parseInt(formData.capacity) < 1) {
      setError('Capacity must be at least 1');
      return false;
    }

    if (parseInt(formData.price_coins) < 0 || parseInt(formData.price_cash) < 0) {
      setError('Prices cannot be negative');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      await apiClient.post(`/slots/gyms/${gymId}/slots`, {
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        capacity: parseInt(formData.capacity),
        price_coins: parseInt(formData.price_coins),
        price_cash: parseInt(formData.price_cash),
        description: formData.description || null
      });

      ToastService.success('Slot created successfully');
      navigate(`/pricing/gyms/${gymId}/slots`);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create slot';
      setError(message);
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Create New Slot
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Add a single time slot for your gym
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Date */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Time */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Capacity */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Capacity"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>

              {/* Pricing */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price (Coins)"
                  name="price_coins"
                  value={formData.price_coins}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price (Cash)"
                  name="price_cash"
                  value={formData.price_cash}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                  required
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description (Optional)"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="e.g., Morning open gym, Peak hours, etc."
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{ backgroundColor: '#14B8A6' }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Create Slot'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CreateSlotPage;
